import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages age verification with localStorage persistence.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} confirmButton - The confirm button.
 * @property {HTMLButtonElement} declineButton - The decline button.
 * @property {HTMLButtonElement} [tryAgainButton] - The try again button.
 *
 * @extends Component<Refs>
 */
export class AgeVerificationComponent extends Component {
	requiredRefs = ["dialog", "confirmButton", "declineButton"];

	/**
	 * Local storage key for age verification
	 */
	static STORAGE_KEY = "age_verification";

	_handleConfirm = () => this.handleConfirm();
	_handleDecline = () => this.handleDecline();
	_handleTryAgain = () => this.resetFromDeclined();

	connectedCallback() {
		super.connectedCallback();

		// Skip verification in the Shopify theme editor so previews aren't blocked
		if (window.Shopify?.designMode) {
			return;
		}

		// Skip verification for search engine bots (SEO friendly)
		if (this.isBot()) {
			return;
		}

		// Check if we should show the verification dialog
		if (this.shouldShowVerification()) {
			this.showDialog();
		}

		this.refs.confirmButton?.addEventListener("click", this._handleConfirm);
		this.refs.declineButton?.addEventListener("click", this._handleDecline);
		this.refs.tryAgainButton?.addEventListener("click", this._handleTryAgain);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.refs.confirmButton?.removeEventListener("click", this._handleConfirm);
		this.refs.declineButton?.removeEventListener("click", this._handleDecline);
		this.refs.tryAgainButton?.removeEventListener(
			"click",
			this._handleTryAgain,
		);
	}

	/**
	 * Get the verification interval in days from the data attribute
	 */
	get verificationInterval() {
		return parseInt(this.getAttribute("data-interval") || "30", 10);
	}

	/**
	 * Check if the current visitor is a search engine bot
	 */
	isBot() {
		const botPatterns = [
			"googlebot",
			"bingbot",
			"slurp",
			"duckduckbot",
			"baiduspider",
			"yandexbot",
			"sogou",
			"exabot",
			"facebot",
			"ia_archiver",
			"lighthouse",
			"chrome-lighthouse",
			"gtmetrix",
			"pingdom",
			"prerender",
			"headlesschrome",
			"semrushbot",
			"ahrefsbot",
			"mj12bot",
			"dotbot",
			"rogerbot",
		];

		const userAgent = navigator.userAgent.toLowerCase();
		return botPatterns.some((bot) => userAgent.includes(bot));
	}

	/**
	 * Get the redirect URL for declined verification
	 */
	get declineUrl() {
		return this.getAttribute("data-decline-url") || "https://www.google.com";
	}

	/**
	 * Check if verification should be shown based on localStorage
	 */
	shouldShowVerification() {
		try {
			const storedData = localStorage.getItem(
				AgeVerificationComponent.STORAGE_KEY,
			);

			if (!storedData) {
				return true;
			}

			const { timestamp } = JSON.parse(storedData);
			const now = Date.now();
			const intervalMs = this.verificationInterval * 24 * 60 * 60 * 1000; // Convert days to milliseconds

			// Check if the interval has passed
			return now - timestamp > intervalMs;
		} catch (error) {
			console.error("Age verification storage error:", error);
			return true; // Show verification if there's an error
		}
	}

	/**
	 * Save verification to localStorage
	 */
	saveVerification() {
		try {
			const data = {
				timestamp: Date.now(),
				verified: true,
			};
			localStorage.setItem(
				AgeVerificationComponent.STORAGE_KEY,
				JSON.stringify(data),
			);
		} catch (error) {
			console.error("Failed to save age verification:", error);
		}
	}

	/**
	 * Handle confirm button click
	 */
	handleConfirm() {
		this.saveVerification();
		this.closeDialog();
	}

	/**
	 * Handle decline button click
	 */
	handleDecline() {
		this.showDeclinedState();
	}

	/**
	 * Show the declined state with blank overlay
	 */
	showDeclinedState() {
		const { dialog } = this.refs;
		if (!dialog) return;

		// Hide the main content and show declined state
		dialog.setAttribute("data-declined", "");
	}

	/**
	 * Reset from declined state
	 */
	resetFromDeclined() {
		const { dialog } = this.refs;
		if (!dialog) return;

		dialog.removeAttribute("data-declined");
	}

	/**
	 * Shows the dialog.
	 */
	showDialog() {
		const { dialog } = this.refs;

		if (!dialog || dialog.open) return;

		// Prevent scrolling when dialog is open
		document.body.style.overflow = "hidden";

		dialog.showModal();
		dialog.setAttribute("data-open", "");
	}

	/**
	 * Closes the dialog.
	 * @param {Function} [callback] - Optional callback to run after dialog closes
	 */
	closeDialog(callback) {
		const { dialog } = this.refs;

		if (!dialog || !dialog.open) return;

		dialog.removeAttribute("data-open");

		onAnimationEnd(dialog, () => {
			dialog.close();

			// Re-enable scrolling
			document.body.style.overflow = "";

			if (callback) {
				callback();
			}
		});
	}
}

if (!customElements.get("age-verification")) {
	customElements.define("age-verification", AgeVerificationComponent);
}
